import { alpha, type Theme } from "@mui/material";
import type { TranslationKeys } from "../../i18n/types";
import { NEUTRAL, STATUS, STATUS_RESCHEDULE } from "../../theme/tokens";

/**
 * Shared taxonomy for the two-tier grouped header band, used by BOTH the ops-backoffice dashboard and
 * the Management Center multi-facility grid (whose column set is a documented subset of the ops one, so
 * the two share almost every field name). One source of truth for: which band each metric field belongs
 * to, the band labels, and the band colors — so the two grids read identically.
 *
 * This is deliberately independent of `columnPickerGroup` (the picker's sections): the band is a finer,
 * metric-level grouping (HPPD / PPD / Overtime …) than the picker's coarse sections (Metadata /
 * Staffing …). A column can carry both.
 */
export type MetricColumnGroupKey =
	| "account"
	| "systems"
	| "milestones"
	| "staffAdoption"
	| "adminActivity"
	| "requestVolume"
	| "requestOutcomes"
	| "census"
	| "hppd"
	| "ppd"
	| "prn"
	| "overtime"
	| "agency";

/** Band labels, translated at the call site via `t(METRIC_GROUP_LABEL_KEYS[key])`. */
export const METRIC_GROUP_LABEL_KEYS = {
	account: "ops.backoffice.colgroup.account",
	systems: "ops.backoffice.colgroup.systems",
	milestones: "ops.backoffice.colgroup.milestones",
	staffAdoption: "ops.backoffice.colgroup.staff.adoption",
	adminActivity: "ops.backoffice.colgroup.admin.activity",
	requestVolume: "ops.backoffice.colgroup.request.volume",
	requestOutcomes: "ops.backoffice.colgroup.request.outcomes",
	census: "ops.backoffice.colgroup.census",
	hppd: "ops.backoffice.colgroup.hppd",
	ppd: "ops.backoffice.colgroup.ppd",
	prn: "ops.backoffice.colgroup.prn",
	overtime: "ops.backoffice.colgroup.overtime",
	agency: "ops.backoffice.colgroup.agency",
} as const satisfies Record<MetricColumnGroupKey, TranslationKeys>;

/**
 * Field id → band. Keyed by the shared metric field names both grids use (the ops row is the superset).
 * The pinned identity columns (`departmentName`, and ops' frozen `network`) are intentionally ABSENT:
 * they render under a blank band cell. `network` in particular must stay ungrouped — it is sticky-frozen
 * in the ops grid, and a band spanning it plus the scrolling `account` columns could not stay pinned.
 * Trend companion columns are not listed here; they inherit their parent metric's band at build time.
 */
export const FIELD_TO_METRIC_GROUP: Record<string, MetricColumnGroupKey> = {
	// Account
	stage: "account",
	opsOwnerId: "account",
	notes: "account",
	// Systems
	hrisLabel: "systems",
	emrLabel: "systems",
	hasDataLoad: "systems",
	// Milestones
	kickOffDate: "milestones",
	trainingDate: "milestones",
	onboardingDate: "milestones",
	nextMeeting: "milestones",
	// Staff adoption
	loggedInPercent: "staffAdoption",
	wauCount: "staffAdoption",
	wauPercent: "staffAdoption",
	bedsideLoginPercent: "staffAdoption",
	// Admin activity
	adminActiveDays: "adminActivity",
	adminActionsCount: "adminActivity",
	// Admin activity — role-scoped counts (multi-facility grid only)
	schedulerLoginCount: "adminActivity",
	schedulerActionsCount: "adminActivity",
	adminLoginCount: "adminActivity",
	headNurseDaysSinceLogin: "adminActivity",
	dailyPrintoutCount: "adminActivity",
	// Request volume
	requestCount: "requestVolume",
	pickupsOffered: "requestVolume",
	openShiftsCount: "requestVolume",
	shiftsPostedCount: "requestVolume",
	replaceShiftCount: "requestVolume",
	// Request outcomes
	filledPercent: "requestOutcomes",
	approvedCount: "requestOutcomes",
	approvedPercent: "requestOutcomes",
	shiftPickupPercent: "requestOutcomes",
	staffPickupPercent: "requestOutcomes",
	firstPickupDate: "requestOutcomes",
	// Census
	avgDailyCensus: "census",
	// HPPD
	targetHppd: "hppd",
	avgScheduledHppd: "hppd",
	avgActualHppd: "hppd",
	hppdDiffFromTarget: "hppd",
	// HPPD — by role and agency split (multi-facility grid; off by default)
	avgActualHppdCna: "hppd",
	avgActualHppdLpn: "hppd",
	avgActualHppdRn: "hppd",
	avgActualHppdAgency: "hppd",
	avgActualHppdNonAgency: "hppd",
	// PPD
	targetPpd: "ppd",
	avgScheduledPpd: "ppd",
	avgActualPpd: "ppd",
	ppdDiffFromTarget: "ppd",
	// PPD — by role and agency split (multi-facility grid; off by default)
	avgActualPpdCna: "ppd",
	avgActualPpdLpn: "ppd",
	avgActualPpdRn: "ppd",
	avgActualPpdAgency: "ppd",
	avgActualPpdNonAgency: "ppd",
	// PRN
	prnScheduledPercent: "prn",
	prnActualPercent: "prn",
	// Overtime
	actualOtHours: "overtime",
	otHoursPercent: "overtime",
	otCostPercent: "overtime",
	structuralOtHours: "overtime",
	// Overtime — per-role breakdown (multi-facility grid; off by default)
	actualOtHoursCna: "overtime",
	actualOtHoursLpn: "overtime",
	actualOtHoursRn: "overtime",
	otHoursPercentCna: "overtime",
	otHoursPercentLpn: "overtime",
	otHoursPercentRn: "overtime",
	// Agency
	actualAgencyHours: "agency",
	agencyHoursPercent: "agency",
	agencyCostPercent: "agency",
} as const;

/** The band a field belongs to, or `undefined` for an ungrouped (identity) column. */
export const metricGroupForField = (field: string): MetricColumnGroupKey | undefined => FIELD_TO_METRIC_GROUP[field];

/**
 * Canonical left→right order of the grouped metric fields — the map's insertion order, which lists each
 * band's fields together and in reading order (e.g. HPPD as target → scheduled → actual → diff). A grid
 * that wants its DEFAULT layout to render as merged bands sorts its columns by this (identity columns
 * first). Fields absent here (a grid's own extras) are left where they are.
 */
export const METRIC_FIELD_ORDER: readonly string[] = Object.keys(FIELD_TO_METRIC_GROUP);

// Two alpha steps of one family hue. Adjacent bands within a family alternate between them so their
// boundary always reads even though they share a hue; the label carries the identity. Across families
// the hue itself changes, so the step there is immaterial. Kept low so the bands stay a quiet backdrop
// behind the traffic-light metric cells rather than competing with them.
const BAND_ALPHA_STRONG = 0.16;
const BAND_ALPHA_SOFT = 0.08;
// Grey reads faint at those alphas, so its family gets its own slightly heavier pair.
const GREY_ALPHA_STRONG = 0.14;
const GREY_ALPHA_SOFT = 0.07;

/**
 * Which family hue + shade each band uses. Four families, none of them the red/amber/green the metric
 * cells already spend on status, so the bands never fight the traffic-light badges:
 *  - context (grey)       — Account, Systems, Milestones
 *  - engagement (violet)  — Staff adoption, Admin activity
 *  - requests (info blue) — Request volume, Request outcomes
 *  - productivity (brand) — Census, HPPD, PPD, PRN, Overtime, Agency (the largest family; the brand hue
 *    also lets the metric-heavy zone pick up the tenant's accent)
 * `shade` alternates within each family in canonical column order so neighbours differ.
 */
type Family = "context" | "engagement" | "requests" | "productivity";
const GROUP_FAMILY: Record<MetricColumnGroupKey, { family: Family; shade: "strong" | "soft" }> = {
	account: { family: "context", shade: "strong" },
	systems: { family: "context", shade: "soft" },
	milestones: { family: "context", shade: "strong" },
	staffAdoption: { family: "engagement", shade: "strong" },
	adminActivity: { family: "engagement", shade: "soft" },
	requestVolume: { family: "requests", shade: "strong" },
	requestOutcomes: { family: "requests", shade: "soft" },
	census: { family: "productivity", shade: "strong" },
	hppd: { family: "productivity", shade: "soft" },
	ppd: { family: "productivity", shade: "strong" },
	prn: { family: "productivity", shade: "soft" },
	overtime: { family: "productivity", shade: "strong" },
	agency: { family: "productivity", shade: "soft" },
};

/**
 * Resolve the band background for every group against the live theme (so the productivity family tracks
 * the tenant's brand color). All values are theme-derived — no hardcoded band colors. Pass the result as
 * `SystemDataGrid`'s `headerGroups.colors`.
 */
export const buildMetricGroupColors = (theme: Theme): Record<MetricColumnGroupKey, string> => {
	const hueFor = (family: Family): string => {
		switch (family) {
			case "context":
				return NEUTRAL[600];
			case "engagement":
				return STATUS_RESCHEDULE.main;
			case "requests":
				return STATUS.INFO.main;
			case "productivity":
				return theme.palette.primary.main;
		}
	};
	const colors = {} as Record<MetricColumnGroupKey, string>;
	for (const key of Object.keys(GROUP_FAMILY) as MetricColumnGroupKey[]) {
		const { family, shade } = GROUP_FAMILY[key];
		const strong = family === "context" ? GREY_ALPHA_STRONG : BAND_ALPHA_STRONG;
		const soft = family === "context" ? GREY_ALPHA_SOFT : BAND_ALPHA_SOFT;
		colors[key] = alpha(hueFor(family), shade === "strong" ? strong : soft);
	}
	return colors;
};

/**
 * Ready-to-pass `headerGroups` config (labels + colors) for either grid. `t` translates the labels;
 * `theme` resolves the colors. Exactly the shape `SystemDataGrid.headerGroups` expects.
 */
export const buildMetricHeaderGroups = (
	t: (key: TranslationKeys) => string,
	theme: Theme,
): { labels: Record<string, string>; colors: Record<string, string> } => ({
	labels: Object.fromEntries((Object.keys(METRIC_GROUP_LABEL_KEYS) as MetricColumnGroupKey[]).map((key) => [key, t(METRIC_GROUP_LABEL_KEYS[key])])),
	colors: buildMetricGroupColors(theme),
});
